import React, { useState } from "react";
import { Box, IconButton, Popover, Typography, Divider } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

/**
 * HelpHint — tiny in-house MUI helper that replaces the legacy IntroJS tour
 * with a per-page help icon. The user clicks the icon to reveal a short
 * hand-authored list of "what you can do on this page" hints.
 *
 * Not a tour. No state machine. No localStorage flag. No step navigation.
 */
export default function HelpHint({ title = "What you can do on this page", hints = [] }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? "help-hint-popover" : undefined;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label={`Help: ${title}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        size="small"
        onClick={handleClick}
        data-testid="help-hint-button"
        sx={{ ml: 1, verticalAlign: "middle" }}
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 360 } } }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          {hints.map((hint, idx) => (
            <li key={idx}>
              <Typography variant="body2">{hint}</Typography>
            </li>
          ))}
        </Box>
      </Popover>
    </>
  );
}
